import React from "react";
import { Link } from "@tanstack/react-router";
import {
  LayoutDashboard,
  GitPullRequestArrow,
  Settings,
  LogOut,
  User,
  Plus,
  Receipt,
  type LucideIcon,
} from "lucide-react";
import { useMeQuery } from "@/queries/queries";
import { logout } from "@/api/auth";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shared/ui/popover";
import { ImpersonationMenuButton } from "@/components/common/ImpersonationMenu";
import { useAddBuilding } from "./useAddBuilding";
import { ACTION_PLAN_PATH } from "./actionPlanLink";
import { usePortalFeatures, type PortalFeatures } from "./usePortalFeatures";

/**
 * Shared nav-item registry: the desktop sidebar and the mobile Sheet drawer
 * (PortalMobileNav) both read it, so a route or label changes in one place.
 */
interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  exact?: boolean;
  /** Shown only while this `usePortalFeatures` switch is on. */
  feature?: keyof PortalFeatures;
}

// NO "Buildings" ITEM: the dedicated portfolio list page was retired
// 2026-05-06 and the Dashboard already ranks buildings by exposure. The detail
// page at /portal/buildings/<id> is unaffected.
//
// NO "Tracker" ITEM since 2026-05-06, but the route still exists at
// /portal/tracker for staff and email-digest deep links.
const NAV_ITEMS: readonly NavItem[] = [
  { label: "Dashboard", to: "/portal", icon: LayoutDashboard, exact: true },
  {
    label: "Action plan",
    to: ACTION_PLAN_PATH,
    icon: GitPullRequestArrow,
    feature: "actionPlan",
  },
];

/**
 * The link list, factored out so the desktop sidebar and the mobile Sheet
 * share styling and active state. `onNavigate` closes the Sheet on tap;
 * desktop never passes it.
 */
export const NavLinks: React.FC<{ onNavigate?: () => void }> = ({
  onNavigate,
}) => {
  const features = usePortalFeatures();
  return (
    <nav className="flex flex-col gap-1 px-3 mt-2">
      {NAV_ITEMS.filter((item) => !item.feature || features[item.feature]).map(
        (item) => (
          <Link
            key={item.to}
            to={item.to}
            activeOptions={{ exact: item.exact }}
            onClick={onNavigate}
            className="flex items-center gap-3 px-3 py-2 text-2xs font-normal text-ink-mute motion-tint hover:text-ink"
            activeProps={{
              className:
                "!bg-paper !text-ink !font-semibold !shadow-[0_1px_2px_rgba(15,15,15,0.04)] hover:!bg-paper",
            }}
          >
            <item.icon className="h-5 w-5 shrink-0" />
            {item.label}
          </Link>
        ),
      )}
    </nav>
  );
};

/**
 * Sidebar shortcut block from Mock 1.
 *   - "+ Add building" opens the AddBuildingDialog in place via
 *     AddBuildingProvider, with no route change.
 *   - "View penalty details" anchors to the dashboard's Buildings section: an
 *     in-page scroll on /portal, a route back to #buildings from anywhere
 *     else.
 */
const QuickActions: React.FC<{ onNavigate?: () => void }> = ({
  onNavigate,
}) => {
  const { setOpen: setAddBuildingOpen } = useAddBuilding();
  // Staff-only (2026-07-23): portfolio composition is managed for the
  // customer, so PM/owner accounts must not see Add Building. Mirrors the
  // dashboard button gate; the BE enforces it with IsAdminUser.
  const { data: user } = useMeQuery();
  const handleAdd = () => {
    setAddBuildingOpen(true);
    onNavigate?.();
  };
  return (
    <div className="px-3 mt-6">
      <div className="px-3 mb-2 text-3xs font-medium uppercase tracking-wider text-ink-mute">
        Quick actions
      </div>
      <div className="flex flex-col gap-1">
        {user?.isStaff && (
          <button
            type="button"
            onClick={handleAdd}
            data-testid="portal-nav-add-building"
            className="flex items-center gap-3 px-3 py-2 rounded-md text-xs font-medium text-paper bg-ink hover:bg-ink/90 motion-tint text-left"
          >
            <Plus className="h-3.5 w-3.5 shrink-0" />
            Add building
          </button>
        )}
        <Link
          to="/portal"
          hash="buildings"
          onClick={onNavigate}
          className="flex items-center gap-3 px-3 py-2 text-xs font-medium text-ink hover:opacity-70 motion-tint"
        >
          <Receipt className="h-4 w-4 shrink-0" />
          View penalty details
        </Link>
      </div>
    </div>
  );
};

/**
 * Bottom-of-nav user pill plus logout/settings popover, shared by the desktop
 * sidebar and the mobile Sheet so the exit options match at every viewport.
 */
export const NavUserMenu: React.FC<{ onNavigate?: () => void }> = ({
  onNavigate,
}) => {
  const { data: user } = useMeQuery();

  const initials =
    [user?.firstName, user?.lastName]
      .filter(Boolean)
      .map((n: string) => n[0])
      .join("")
      .toUpperCase() || "U";

  const handleLogout = () => {
    logout();
    window.location.href = "/login";
  };

  return (
    <>
      {user?.isStaff && (
        <div className="px-3 pb-1 pt-3 border-t border-ink-line">
          <ImpersonationMenuButton userIsStaff />
        </div>
      )}

      <div className="p-3 border-t border-ink-line">
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-3 w-full px-3 py-2 text-xs font-medium text-ink hover:opacity-70 motion-tint"
            >
              <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-ink text-[10px] font-semibold text-paper shrink-0">
                {initials}
              </span>
              <span className="truncate text-left">
                {user?.firstName || user?.email || "User"}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            align="start"
            className="motion-pop w-48 p-1.5"
          >
            <Link
              to="/settings"
              search={{ fromPortal: "1" } as any}
              onClick={onNavigate}
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-ink-body hover:bg-paper-panel motion-tint"
            >
              <Settings className="h-4 w-4" />
              Settings
            </Link>
            {/* Staff-only escape hatch into the Classic Filing shell, as
                TrackerSidebar staff-gates the identical link. For a non-staff
                customer the item would look BROKEN: it carries no
                ?fromPortal=1 explicit-switch flag, so __root's
                accidental-landing guard bounces them straight back to
                /portal. */}
            {user?.isStaff && (
              <Link
                to="/"
                onClick={onNavigate}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-ink-body hover:bg-paper-panel motion-tint"
              >
                <User className="h-4 w-4" />
                Filing View
              </Link>
            )}
            <div className="my-1 border-t border-ink-line" />
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-destructive hover:bg-destructive/10 motion-tint"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </>
  );
};

/**
 * Desktop fixed sidebar, hidden below `lg` (1024px); the mobile shell uses
 * PortalMobileNav instead. Wave-1A responsive sweep, "Critical out-of-scope
 * finding": the 220px reservation dropped BuildingDetail's content width below
 * 100px at 320/375.
 */
const PortalNav: React.FC = () => (
  <div
    className="hidden lg:flex flex-col bg-paper-panel border-r border-ink-line w-[220px] min-h-screen fixed top-0 left-0 z-40"
    data-testid="portal-nav-desktop"
  >
    <div className="flex flex-col items-center gap-1 py-6 px-4">
      {/* The `#16150F` wordmark inverts to a white silhouette in dark, so it
          stays legible on the dark sidebar. */}
      <img
        src="/codeClearLogo.svg"
        alt="Code Clear"
        className="h-8 w-auto dark:brightness-0 dark:invert"
      />
    </div>

    <NavLinks />

    <QuickActions />

    <div className="flex-1" />

    <NavUserMenu />
  </div>
);

export default PortalNav;
